import { useEffect, useMemo, useState } from "react";
import { Download } from "lucide-react";
import { AppDialog } from "@/components/common";
import { Button } from "@/components/ui/button";
import { OrgTreeSelect, SelectField } from "@/components/common";
import { useTran } from "@/hooks";
import { getLevelNumber } from "@/features/organization/utils/organization.helpers";
import { useTargetSetupStore } from "../hooks/useTargetSetupStore";
import {
  exportTargetSetupExcel,
  loadTargetSetupExportSummary,
} from "../operations/target-setup.ops";
import { SearchableSelect } from "@/components/common";
import { useAuthStore } from "@/features/auth";

const FILTER_BY_OPTIONS = [
  { value: "all", label: "All" },
  { value: "businessUnit", label: "Business Unit" },
  { value: "company", label: "Company" },
  { value: "site", label: "Site" },
  { value: "country", label: "Country" },
];
const EXPORT_MODE_OPTIONS = [
  { value: "filled", label: "Filled Targets" },
  { value: "empty", label: "Unfilled Targets" },
  { value: "mixed", label: "All Targets" },
];
const DEFAULT_EXPORT_SUMMARY = {
  filledCount: 0,
  emptyCount: 0,
  mixedCount: 0,
};
const CATEGORY_OPTION_ORDER = [
  "General",
  "Environmental",
  "Social",
  "Governance",
];
// Stable reference so callers that don't pass defaultOrgIds (e.g. TargetSetupPage)
// don't create a fresh array every render, which would otherwise re-trigger the
// org-selection effect below on every render.
const EMPTY_ORG_IDS = [];

const HIERARCHY_MAX_LEVEL_MAP = {
  businessUnit: "level_1",
  company: "level_2",
  site: "level_3",
};

const getNodeValue = (node) => node?.id || node?.name || "";
const normalizeUnitLevel = (node) => node?.unitLevel || node?.unit_level || "";

const resolveCountryLabel = (node) => {
  const value =
    node?.countryLabel ||
    node?.country ||
    node?.location?.country ||
    node?.location?.countryLabel ||
    node?.locationLabel ||
    node?.countryName ||
    node?.address?.country ||
    "";
  if (value) return String(value);
  if (normalizeUnitLevel(node) === "country") {
    return String(node?.name || "");
  }
  return "";
};

const trimTreeByLevel = (
  nodes = [],
  maxLevel,
  { preserveParentCompany = false, preserveCompany = true } = {},
) =>
  nodes
    .map((node) => {
      const unitLevel = normalizeUnitLevel(node);
      if (unitLevel === "parent_company" && !preserveParentCompany) {
        return trimTreeByLevel(node?.children || [], maxLevel, {
          preserveParentCompany,
          preserveCompany,
        });
      }
      if (unitLevel === "company" && !preserveCompany) {
        return trimTreeByLevel(node?.children || [], maxLevel, {
          preserveParentCompany,
          preserveCompany,
        });
      }
      const levelNumber = getLevelNumber(unitLevel);
      if (maxLevel && levelNumber && levelNumber > maxLevel) return null;
      const children = trimTreeByLevel(node?.children || [], maxLevel, {
        preserveParentCompany,
        preserveCompany,
      });
      return { ...node, unitLevel, children };
    })
    .flat()
    .filter(Boolean);

const buildCountryTree = (tree = []) => {
  const countries = new Map();

  const walk = (nodes = []) => {
    nodes.forEach((node) => {
      const label = resolveCountryLabel(node).trim();
      if (label) {
        countries.set(label.toLowerCase(), label);
      }
      if (Array.isArray(node?.children)) walk(node.children);
    });
  };

  walk(tree);

  return Array.from(countries.values()).map((label) => ({
    id: `country:${label}`,
    name: label,
    unitLevel: "country",
  }));
};

const collectTreeValues = (nodes = []) => {
  const values = new Set();

  const walk = (items = []) => {
    items.forEach((node) => {
      const value = getNodeValue(node);
      if (value && !node?.isGroup) {
        values.add(String(value));
      }
      if (Array.isArray(node?.children)) walk(node.children);
    });
  };

  walk(nodes);
  return Array.from(values);
};

const collectOrgIdsByCountry = (tree = [], selectedCountries = []) => {
  const selectedCountrySet = new Set(
    (Array.isArray(selectedCountries) ? selectedCountries : [])
      .map((value) =>
        String(value || "")
          .replace(/^country:/i, "")
          .trim()
          .toLowerCase(),
      )
      .filter(Boolean),
  );

  if (selectedCountrySet.size === 0) return [];

  const orgIds = new Set();
  const walk = (nodes = []) => {
    nodes.forEach((node) => {
      const unitLevel = normalizeUnitLevel(node);
      const country = resolveCountryLabel(node).trim().toLowerCase();
      const organizationId = node?.id ? String(node.id) : "";

      if (
        organizationId &&
        unitLevel !== "parent_company" &&
        country &&
        selectedCountrySet.has(country)
      ) {
        orgIds.add(organizationId);
      }

      if (Array.isArray(node?.children)) walk(node.children);
    });
  };

  walk(tree);
  return Array.from(orgIds);
};

const getCurrentCompanyId = (user) =>
  user?.companyId || user?.company?._id || user?.company?.id || user?.orgId;

const normalizeFramework = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

const extractFrameworkOptionCodes = (disclosure) => {
  const values = [
    disclosure?.framework,
    disclosure?.frameworkCode,
    disclosure?.framework_code,
    disclosure?.frameworks,
    disclosure?.frameworkCodes,
    disclosure?.framework_codes,
  ];

  const seen = new Set();
  const codes = [];

  values
    .flatMap((value) => {
      if (!value) return [];
      if (Array.isArray(value)) return value;
      if (typeof value === "object") {
        return [value.code, value.framework, value.value].filter(Boolean);
      }
      if (typeof value === "string") {
        const parts = /[,/|]/.test(value) ? value.split(/[,/|]+/) : [value];
        return parts;
      }

      return [String(value ?? "").trim()];
    })
    .forEach((code) => {
      const key = normalizeFramework(code);
      if (!key || seen.has(key)) return;
      seen.add(key);
      codes.push(code);
    });

  return codes;
};

const ExportTargetSetupDialog = ({
  open,
  onOpenChange,
  defaultYear,
  yearOptions = [],
  defaultFramework = "",
  defaultCategory = "",
  defaultOrgIds = EMPTY_ORG_IDS,
  defaultExportMode = "filled",
  reportingYears = [],
  availableFrameworks = [],
}) => {
  const { t } = useTran();
  const { filteredOrgTree, disclosures } = useTargetSetupStore();
  const { user } = useAuthStore();
  const [year, setYear] = useState(defaultYear || yearOptions[0]?.value || "");
  const [filterBy, setFilterBy] = useState("all");
  const [exportMode, setExportMode] = useState("filled");
  const [framework, setFramework] = useState("");
  const [category, setCategory] = useState("");
  const [selectedOrganizations, setSelectedOrganizations] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [exportSummary, setExportSummary] = useState(DEFAULT_EXPORT_SUMMARY);
  const [summaryError, setSummaryError] = useState(false);
  const companyId = getCurrentCompanyId(user) || "";

  const [selectedReportingYear, setSelectedReportingYear] = useState(null);
  const activeYearDisclosures = useMemo(() => {
    if (!selectedReportingYear?.startYear) {
      return Array.isArray(disclosures) ? disclosures : [];
    }

    const selectedYear = String(selectedReportingYear.startYear);
    return (Array.isArray(disclosures) ? disclosures : []).filter((item) => {
      const masterYears = [
        ...(Array.isArray(item?.masterDataYears) ? item.masterDataYears : []),
        item?.masterDataYear,
        ...(Array.isArray(item?.masterDataRecords)
          ? item.masterDataRecords.map((record) => record?.year)
          : []),
      ];

      return masterYears.some((year) => String(year) === selectedYear);
    });
  }, [disclosures, selectedReportingYear]);

  const frameworkLabelByCode = useMemo(
    () =>
      new Map(
        (Array.isArray(availableFrameworks) ? availableFrameworks : [])
          .map((framework) => {
            const code =
              framework?.code ||
              framework?.framework ||
              framework?.value ||
              framework?.id;
            if (!code) return null;
            return [
              normalizeFramework(code),
              String(framework?.name || framework?.label || code).trim(),
            ];
          })
          .filter(Boolean),
      ),
    [availableFrameworks],
  );

  const frameworkOptions = useMemo(() => {
    const seen = new Set();
    const disclosureFrameworks = activeYearDisclosures
      .flatMap(extractFrameworkOptionCodes)
      .filter((code) => {
        const key = normalizeFramework(code);
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .sort((left, right) => {
        const labelLeft =
          frameworkLabelByCode.get(normalizeFramework(left)) || left;
        const labelRight =
          frameworkLabelByCode.get(normalizeFramework(right)) || right;
        return String(labelLeft).localeCompare(String(labelRight));
      });

    const options = disclosureFrameworks.map((framework) => ({
      value: framework,
      label:
        frameworkLabelByCode.get(normalizeFramework(framework)) || framework,
    }));

    // Ensure the incoming default (e.g. JJ-Group's synthetic framework value,
    // or a framework not yet reflected in this year's disclosures) is always
    // selectable — otherwise the picker silently shows blank for it.
    if (
      defaultFramework &&
      !options.some(
        (option) =>
          normalizeFramework(option.value) ===
          normalizeFramework(defaultFramework),
      )
    ) {
      options.unshift({
        value: defaultFramework,
        label:
          frameworkLabelByCode.get(normalizeFramework(defaultFramework)) ||
          defaultFramework,
      });
    }

    return options;
  }, [activeYearDisclosures, frameworkLabelByCode, defaultFramework]);

  useEffect(() => {
    if (!open || !reportingYears?.length || !defaultYear) {
      return;
    }

    const defaultReportingYear =
      reportingYears.find(
        (ry) => String(ry.startYear) === String(defaultYear),
      ) || reportingYears[0];

    setSelectedReportingYear(defaultReportingYear);
    setYear(String(defaultReportingYear.startYear));
  }, [open, reportingYears, defaultYear]);

  useEffect(() => {
    if (!open) return;
    setFilterBy("all");
    setExportMode(defaultExportMode || "filled");
    setFramework(defaultFramework || frameworkOptions[0]?.value || "");
    setCategory(defaultCategory || "");
    setExportSummary(DEFAULT_EXPORT_SUMMARY);
    setSummaryError(false);
  }, [
    defaultExportMode,
    defaultFramework,
    defaultCategory,
    open,
    yearOptions,
    frameworkOptions,
  ]);

  const categoryOptions = useMemo(() => {
    // const presentCategories = new Set(
    //   (Array.isArray(disclosures) ? disclosures : [])
    //     .map((item) => String(item?.category || "").trim())
    //     .filter(Boolean),
    // );

    // const ordered = CATEGORY_OPTION_ORDER.filter((value) =>
    //   presentCategories.has(value),
    // ).map((value) => ({
    //   value,
    //   label: t(value),
    // }));

    // const extras = Array.from(presentCategories)
    //   .filter((value) => !CATEGORY_OPTION_ORDER.includes(value))
    //   .sort((left, right) => left.localeCompare(right))
    //   .map((value) => ({
    //     value,
    //     label: t(value),
    //   }));

    return [
      { value: "", label: t("All Categories") },
      ...CATEGORY_OPTION_ORDER.map((value) => ({
        value,
        label: t(value),
      })),
    ];
  }, [t]);

  const reportingYearOrganizationIds = useMemo(() => {
    const orgIds = (selectedReportingYear?.organizations || []).map((org) =>
      String(org._id),
    );

    if (
      selectedReportingYear?.isCompanySelected &&
      companyId &&
      !selectedReportingYear?.isAllOrganization
    ) {
      orgIds.push(String(companyId));
    }

    return Array.from(new Set(orgIds));
  }, [selectedReportingYear, companyId]);

  const reportingYearOrganizationTree = useMemo(() => {
    if (!selectedReportingYear) return [];

    if (selectedReportingYear.isAllOrganization) {
      return filteredOrgTree;
    }

    const allowedSet = new Set(reportingYearOrganizationIds.map(String));

    const getNodeId = (node) => String(node.id || node._id || node.key);

    const filterNode = (node) => {
      const children = (node.children || []).map(filterNode).filter(Boolean);

      const nodeId = getNodeId(node);
      const isAllowed = allowedSet.has(nodeId);

      // show node if it is allowed OR any child is allowed
      if (isAllowed || children.length > 0) {
        return {
          ...node,
          children,
        };
      }

      return null;
    };

    return (filteredOrgTree || []).map(filterNode).filter(Boolean);
  }, [filteredOrgTree, selectedReportingYear, reportingYearOrganizationIds]);

  const updateSelectedOrganizations = (values) => {
    // An empty allowed list means no restriction (e.g. a reporting year
    // marked isAllOrganization) — same convention as
    // reportingYearOrganizationTree above. Only filter when there's an
    // actual curated list the values need to belong to.
    if (reportingYearOrganizationIds.length === 0) {
      setSelectedOrganizations(values);
      return;
    }
    const allowedIds = new Set(
      reportingYearOrganizationIds.map((id) => String(id)),
    );
    setSelectedOrganizations(values.filter((id) => allowedIds.has(String(id))));
  };

  const selectorTree = useMemo(() => {
    const filteredTree = reportingYearOrganizationTree;

    if (filterBy === "country") {
      return buildCountryTree(filteredTree);
    }

    const maxUnitLevel = HIERARCHY_MAX_LEVEL_MAP[filterBy];
    const maxLevel = maxUnitLevel ? getLevelNumber(maxUnitLevel) : null;

    return trimTreeByLevel(filteredTree, maxLevel, {
      preserveParentCompany: filterBy === "all",
      preserveCompany: filterBy !== "country",
    });
  }, [filterBy, reportingYearOrganizationTree]);

  useEffect(() => {
    if (!open) return;
    if (filterBy === "all" && defaultOrgIds.length > 0) {
      updateSelectedOrganizations(defaultOrgIds);
      return;
    }
    updateSelectedOrganizations(collectTreeValues(selectorTree));
  }, [open, selectorTree, filterBy, defaultOrgIds]);

  const selectedOrganizationIds = useMemo(() => {
    if (filterBy === "country") {
      return collectOrgIdsByCountry(filteredOrgTree, selectedOrganizations);
    }
    return Array.from(
      new Set(
        (Array.isArray(selectedOrganizations) ? selectedOrganizations : [])
          .map((value) => String(value || "").trim())
          .filter(Boolean),
      ),
    );
  }, [filterBy, filteredOrgTree, selectedOrganizations]);

  const organizationSummary = useMemo(() => {
    if (!selectedOrganizations.length) {
      return filterBy === "country"
        ? t("Select countries")
        : t("Select organizations");
    }
    if (selectedOrganizations.length === 1) {
      return undefined;
    }
    return filterBy === "country"
      ? `${selectedOrganizations.length} ${t("countries selected")}`
      : `${selectedOrganizations.length} ${t("organizations selected")}`;
  }, [filterBy, selectedOrganizations.length, t]);

  useEffect(() => {
    if (!open) return undefined;

    if (!year || selectedOrganizationIds.length === 0) {
      setLoadingSummary(false);
      setExportSummary(DEFAULT_EXPORT_SUMMARY);
      setSummaryError(false);
      return undefined;
    }

    let cancelled = false;

    setLoadingSummary(true);
    setSummaryError(false);
    setExportSummary(DEFAULT_EXPORT_SUMMARY);

    void loadTargetSetupExportSummary({
      year: Number(year),
      organizationIds: selectedOrganizationIds,
      frameworks: framework ? [framework] : [],
      categories: category ? [category] : [],
    })
      .then((summary) => {
        if (cancelled) return;
        if (!summary) {
          setSummaryError(true);
          setExportSummary(DEFAULT_EXPORT_SUMMARY);
          return;
        }
        setExportSummary({
          filledCount: Number(summary.filledCount) || 0,
          emptyCount: Number(summary.emptyCount) || 0,
          mixedCount: Number(summary.mixedCount) || 0,
        });
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingSummary(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [category, framework, open, selectedOrganizationIds, year]);

  const selectedModeCount = useMemo(() => {
    if (exportMode === "empty") return exportSummary.emptyCount;
    if (exportMode === "mixed") return exportSummary.mixedCount;
    return exportSummary.filledCount;
  }, [
    exportMode,
    exportSummary.emptyCount,
    exportSummary.filledCount,
    exportSummary.mixedCount,
  ]);

  const handleDownload = async () => {
    setExporting(true);
    try {
      const result = await exportTargetSetupExcel({
        year: Number(year),
        organizationIds: selectedOrganizationIds,
        frameworks: framework ? [framework] : [],
        categories: category ? [category] : [],
        exportMode,
      });
      if (result?.fileName) {
        onOpenChange(false);
      }
    } finally {
      setExporting(false);
    }
  };

  const canDownload =
    Boolean(year) &&
    selectedOrganizationIds.length > 0 &&
    !exporting &&
    !loadingSummary &&
    !summaryError &&
    selectedModeCount > 0;

  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title={
        <span className="flex items-center gap-2">
          <Download className="h-5 w-5 text-primary" />
          {t("Excel Export")}
        </span>
      }
      footer={
        <div className="flex w-full items-center justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={exporting}
          >
            {t("Cancel")}
          </Button>
          <Button
            type="button"
            onClick={handleDownload}
            disabled={!canDownload}
          >
            {exporting ? t("Exporting...") : t("Export Excel")}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="space-y-1">
          <p className="text-sm font-medium text-foreground">
            {t("Reporting Year")}
          </p>
          <SearchableSelect
            value={
              selectedReportingYear?.startYear
                ? String(selectedReportingYear.startYear)
                : ""
            }
            onChange={(value) => {
              const selected = reportingYears.find(
                (ry) => String(ry.startYear) === String(value),
              );

              setYear(value);
              setSelectedReportingYear(selected || null);
            }}
            options={yearOptions}
            placeholder={t("Select Reporting Year")}
          />
        </div>

        <div className="space-y-1">
          <p className="text-sm font-medium text-foreground">
            {t("Export Mode")}
          </p>
          <SearchableSelect
            value={exportMode}
            onChange={setExportMode}
            options={EXPORT_MODE_OPTIONS.map((option) => ({
              value: option.value,
              label: t(option.label),
            }))}
            placeholder={t("Select Export Mode")}
          />
        </div>

        <div className="space-y-1">
          <p className="text-sm font-medium text-foreground">
            {t("Framework")}
          </p>
          <SearchableSelect
            value={framework}
            onChange={setFramework}
            options={frameworkOptions}
            placeholder={t("Select Framework")}
          />
        </div>

        <div className="space-y-1">
          <p className="text-sm font-medium text-foreground">{t("Category")}</p>
          <SearchableSelect
            value={category}
            onChange={setCategory}
            options={categoryOptions}
            placeholder={t("Select Category")}
          />
        </div>

        <div className="space-y-1">
          <p className="text-sm font-medium text-foreground">
            {t("Organizations")}
          </p>
          <OrgTreeSelect
            tree={selectorTree}
            value={selectedOrganizations}
            onChange={updateSelectedOrganizations}
            multiSelect
            showBranchSelectAll
            showAllOption={false}
            placeholder={organizationSummary || t("Select organizations")}
            helperText={null}
            className="h-9 text-[10px]"
            allowOrgs={reportingYearOrganizationIds}
          />
        </div>
        <div className="h-[35px]">
          {loadingSummary ? (
            <div className="rounded-lg border border-border bg-muted/20 text-xs px-3 py-2 text-muted-foreground">
              {/* <p>
              {t(
                "Disclosures are resolved automatically from metric assignments for the selected year.",
              )}
            </p>
            <p>
              {t("Organizations in export scope")}:{" "}
              <strong>{selectedOrganizationIds.length}</strong>
            </p> */}
              {/* <p>
              {t("Filled rows")}: <strong>{exportSummary.filledCount}</strong>
              {" • "}
              {t("Empty rows")}: <strong>{exportSummary.emptyCount}</strong>
              {" • "}
              {t("Mixed rows")}: <strong>{exportSummary.mixedCount}</strong>
            </p> */}
              <p>{t("Checking export scope...")}</p>
            </div>
          ) : (
            <></>
          )}

          <div className="text-xs text-muted-foreground">
            {selectedOrganizations.length == 0 ? (
              <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-red-700">
                {t("Please select organizations")}
              </p>
            ) : null}
            {!loadingSummary &&
            summaryError &&
            selectedOrganizations.length > 0 ? (
              <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-red-700">
                {t("Unable to load export summary right now.")}
              </p>
            ) : null}
            {!loadingSummary &&
            !summaryError &&
            selectedOrganizations.length > 0 &&
            selectedModeCount === 0 ? (
              <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-red-700">
                {t(
                  "There are no metrics for the selected year, organizations, and export mode.",
                )}
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </AppDialog>
  );
};

export default ExportTargetSetupDialog;
